import { apiService } from '@/api/apiConfig';
import {
  AccountingCenterCreationWithAllocationsData,
  AccountingCenterCreationResponse,
  AccountingCenterUpdateWithAllocationsData,
  AccountingCenterUpdateResponse,
  AccountingCenterResponse,
  AccountingCentersResponse
} from '@/services/types/accounting-centers-types';

const handleAccountingCenterError = (error: any): Error => {
  if (error instanceof Error && error.message) {
    return error;
  }
  if (error.response?.data?.message) {
    return new Error(error.response.data.message);
  }
  return new Error('Accounting center operation failed');
};

export const accountingCenterService = {
  getAccountingCenters: async (budgetId: string): Promise<AccountingCentersResponse> => {
    try {
      return await apiService.request<AccountingCentersResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/accounting-center`
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  getAccountingCentersByType: async (budgetId: string, type: string): Promise<AccountingCentersResponse> => {
    try {
      return await apiService.request<AccountingCentersResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/accounting-center`,
        params: {
          type
        }
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  getAccountingCenter: async (budgetId: string, accountingCenterId: string): Promise<AccountingCenterResponse> => {
    try {
      return await apiService.request<AccountingCenterResponse>({
        method: 'GET',
        url: `/budget/${budgetId}/accounting-center/${accountingCenterId}`
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  createAccountingCenter: async (
    budgetId: string,
    accountingCenterData: AccountingCenterCreationWithAllocationsData
  ): Promise<AccountingCenterCreationResponse> => {
    try {
      return await apiService.request<AccountingCenterCreationResponse>({
        method: 'POST',
        url: `/budget/${budgetId}/accounting-center`,
        data: accountingCenterData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  activateACs: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/accounting-center/activate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  deactivateAC: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'POST',
        url: `/budget/${budgetId}/accounting-center/deactivate`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  updateAccountingCenter: async (
    budgetId: string,
    accountingCenterId: string,
    accountingCenterData: AccountingCenterUpdateWithAllocationsData
  ): Promise<AccountingCenterUpdateResponse> => {
    try {
      return await apiService.request<AccountingCenterUpdateResponse>({
        method: 'PATCH',
        url: `/budget/${budgetId}/accounting-center/${accountingCenterId}`,
        data: accountingCenterData,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  deleteAccountingCenter: async (budgetId: string, accountingCenterId: string): Promise<void> => {
    try {
      return await apiService.request<void>({
        method: 'DELETE',
        url: `/budget/${budgetId}/accounting-center/${accountingCenterId}`
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  adjustAcmevAllocation: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'PATCH',
        url: `/budget/${budgetId}/accounting-center/acmev/adjust-allocation`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  },

  adjustAcrtAllocation: async (budgetId: string, data: any): Promise<any> => {
    try {
      return await apiService.request<any>({
        method: 'PATCH',
        url: `/budget/${budgetId}/accounting-center/acrt/adjust-allocation`,
        data,
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      throw handleAccountingCenterError(error);
    }
  }
};
